import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  Autocomplete,
  Divider,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TuneIcon from '@mui/icons-material/Tune';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoIcon from '@mui/icons-material/Info';

const TemplateParameterControls = ({ 
  template, 
  onExecute, 
  isLoading = false, 
  defaultExpanded = false,
  brandId // Pass brand ID for fetching field options
}) => {
  const [parameters, setParameters] = useState({});
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [parameterSchema, setParameterSchema] = useState({});
  const [uiConfig, setUiConfig] = useState({});
  const [fieldOptions, setFieldOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Fetch dynamic field options from backend
  useEffect(() => {
    if (brandId) {
      fetchFieldOptions();
    }
  }, [brandId]);

  const fetchFieldOptions = async () => {
    try {
      setLoadingOptions(true);
      const token = localStorage.getItem('token');
      
      // Build URL with templateId if available
      const url = template?.id 
        ? `/api/chat/templates/field-options/${brandId}?templateId=${template.id}`
        : `/api/chat/templates/field-options/${brandId}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFieldOptions(data.options || {});
        console.log('[TemplateParams] Loaded field options:', Object.keys(data.options || {}));
      } else {
        console.warn('[TemplateParams] Failed to load field options:', response.status);
      }
    } catch (error) {
      console.error('[TemplateParams] Error fetching field options:', error);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (template?.parameters) {
      try {
        const schema = JSON.parse(template.parameters);
        setParameterSchema(schema);
        
        // Initialize parameters with defaults
        const initialParams = {};
        Object.entries(schema).forEach(([key, param]) => {
          if (param.default !== undefined) {
            initialParams[key] = param.default;
          }
        });
        setParameters(initialParams);

        // Parse UI config
        if (template.ui_config) {
          setUiConfig(JSON.parse(template.ui_config));
        }
      } catch (error) {
        console.error('Error parsing template parameters:', error);
      }
    }
  }, [template]);

  const handleParameterChange = (paramName, value) => {
    setParameters(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  const handleExecute = () => {
    // Include all parameters (visible + technical) with defaults
    const allParameters = { ...parameters };
    
    // Ensure technical parameters have their default values
    const technicalParams = ['database', 'table', 'table_name', 'database_name', 'schema'];
    Object.entries(parameterSchema).forEach(([key, param]) => {
      if (technicalParams.includes(key.toLowerCase()) && allParameters[key] === undefined) {
        if (param.default !== undefined) {
          allParameters[key] = param.default;
        }
      }
    });
    
    onExecute(template.id, allParameters);
  };

  const renderParameterControl = (paramName, paramDef) => {
    // Ensure we always use the current value from state, or default if not set
    const currentValue = parameters[paramName] !== undefined ? parameters[paramName] : paramDef.default;
    
    switch (paramDef.type) {
      case 'group_by':
        return (
          <FormControl fullWidth size="small" key={paramName}>
            <InputLabel>{paramDef.description || 'Group By'}</InputLabel>
            <Select
              value={currentValue}
              label={paramDef.description || 'Group By'}
              onChange={(e) => handleParameterChange(paramName, e.target.value)}
            >
              {paramDef.options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{option.label}</span>
                    <Tooltip title={`SQL: ${option.sql}`}>
                      <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'metrics':
        return (
          <Autocomplete
            key={paramName}
            multiple
            size="small"
            options={paramDef.options || []}
            getOptionLabel={(option) => option.label}
            value={(paramDef.options || []).filter(option => 
              Array.isArray(currentValue) ? 
                currentValue.includes(option.value) : 
                currentValue === option.value
            )}
            onChange={(event, newValue) => {
              const values = newValue.map(option => option.value);
              handleParameterChange(paramName, values);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip 
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label} 
                  size="small" 
                  color="primary"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={paramDef.description || 'Metrics'}
                placeholder="Select metrics..."
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.sql}
                  </Typography>
                </Box>
              </li>
            )}
          />
        );

      case 'string':
        // Check if dynamic options are available for this field
        const hasDynamicOptions = fieldOptions[paramName] && fieldOptions[paramName].length > 0;
        const hasStaticOptions = paramDef.options && paramDef.options.length > 0;
        
        if (hasDynamicOptions || hasStaticOptions) {
          const options = hasDynamicOptions ? fieldOptions[paramName] : paramDef.options;
          
          // Ensure the current value exists in options, otherwise use first option
          const optionValues = options.map(opt => typeof opt === 'string' ? opt : opt.value);
          const validValue = optionValues.includes(currentValue) ? currentValue : (optionValues[0] || '');
          
          // If we had to change the value to a valid one, update state
          if (validValue !== currentValue && validValue) {
            setTimeout(() => handleParameterChange(paramName, validValue), 0);
          }
          
          return (
            <FormControl fullWidth size="small" key={paramName}>
              <InputLabel>{paramDef.description || paramName}</InputLabel>
              <Select
                value={validValue}
                label={paramDef.description || paramName}
                onChange={(e) => handleParameterChange(paramName, e.target.value)}
                disabled={loadingOptions}
              >
                {options.map(option => (
                  <MenuItem 
                    key={typeof option === 'string' ? option : option.value} 
                    value={typeof option === 'string' ? option : option.value}
                  >
                    {typeof option === 'string' ? option : option.label || option.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        } else {
          // Fallback to text input if no options available
          return (
            <TextField
              key={paramName}
              fullWidth
              size="small"
              label={paramDef.description || paramName}
              value={currentValue}
              onChange={(e) => handleParameterChange(paramName, e.target.value)}
            />
          );
        }

      case 'number':
        return (
          <TextField
            key={paramName}
            fullWidth
            size="small"
            type="number"
            label={paramDef.description || paramName}
            value={currentValue}
            onChange={(e) => handleParameterChange(paramName, parseFloat(e.target.value) || 0)}
          />
        );

      case 'date':
        // Convert relative dates like "today-30" to actual dates for input
        let dateValue = currentValue;
        if (typeof dateValue === 'string' && dateValue.includes('today')) {
          const today = new Date();
          const match = dateValue.match(/today([+-]\d+)?/);
          if (match) {
            const offset = match[1] ? parseInt(match[1]) : 0;
            today.setDate(today.getDate() + offset);
            dateValue = today.toISOString().split('T')[0];
          }
        }
        
        return (
          <Box key={paramName} sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={paramDef.description || paramName}
              value={dateValue}
              onChange={(e) => handleParameterChange(paramName, e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputBase-root': {
                  fontWeight: 'bold',
                  color: 'warning.main'
                }
              }}
            />
            {/* Show relative date hint if using relative format */}
            {typeof currentValue === 'string' && currentValue.includes('today') && (
              <Typography 
                variant="caption" 
                sx={{ 
                  position: 'absolute',
                  top: -18,
                  right: 0,
                  color: 'warning.main',
                  fontFamily: 'monospace',
                  fontSize: '10px',
                  bgcolor: 'warning.light',
                  px: 0.5,
                  borderRadius: 0.5
                }}
              >
                📅 {currentValue.replace('today', 'Today')}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <TextField
            key={paramName}
            fullWidth
            size="small"
            label={paramDef.description || paramName}
            value={currentValue}
            onChange={(e) => handleParameterChange(paramName, e.target.value)}
          />
        );
    }
  };

  const getVisibleParameters = () => {
    // Filter out technical parameters (database, table names, etc.)
    const technicalParams = ['database', 'table', 'table_name', 'database_name', 'schema'];
    
    let params = Object.entries(parameterSchema);
    
    // First, filter out technical parameters
    params = params.filter(([paramName]) => 
      !technicalParams.includes(paramName.toLowerCase())
    );
    
    // Then apply UI config filtering if present
    if (uiConfig.parameter_controls) {
      params = params.filter(([paramName, paramDef]) => {
        const uiControl = uiConfig.parameter_controls[paramName];
        return uiControl?.show_in_ui !== false;
      });
    }
    
    return params;
  };

  const hasDynamicParameters = Object.values(parameterSchema).some(param => 
    param.type === 'group_by' || param.type === 'metrics'
  );

  if (!template) {
    return null;
  }

  // If no parameters, just show Execute button
  if (Object.keys(parameterSchema).length === 0) {
    return (
      <Button
        variant="contained"
        startIcon={isLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
        onClick={handleExecute}
        disabled={isLoading}
        size="small"
        sx={{ mt: 1 }}
      >
        {isLoading ? 'Executing...' : 'Execute'}
      </Button>
    );
  }

  const visibleParams = getVisibleParameters();
  
  if (visibleParams.length === 0) {
    return (
      <Button
        variant="contained"
        startIcon={<PlayArrowIcon />}
        onClick={handleExecute}
        disabled={isLoading}
        size="small"
      >
        {isLoading ? <CircularProgress size={16} /> : 'Execute Template'}
      </Button>
    );
  }

  return (
    <Paper sx={{ mb: 2 }}>
      <Accordion expanded={expanded} onChange={(e, isExpanded) => setExpanded(isExpanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TuneIcon fontSize="small" />
            <Typography variant="subtitle2">Template Parameters</Typography>
            {hasDynamicParameters && (
              <Chip label="Dynamic" size="small" color="primary" />
            )}
            {!expanded && (
              <Typography variant="caption" color="text.secondary">
                ({visibleParams.length} parameters)
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {hasDynamicParameters && (
              <Alert severity="info" sx={{ mb: 1 }}>
                This template supports dynamic grouping and metrics selection.
                Customize the parameters below to change how data is analyzed and displayed.
              </Alert>
            )}
            
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
              {visibleParams.map(([paramName, paramDef]) => (
                <Box key={paramName}>
                  {renderParameterControl(paramName, paramDef)}
                  {paramDef.type === 'group_by' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Changes how data is grouped in results
                    </Typography>
                  )}
                  {paramDef.type === 'metrics' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Select which metrics to calculate
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            <Divider />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {template.name}
              </Typography>
              <Button
                variant="contained"
                startIcon={isLoading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                onClick={handleExecute}
                disabled={isLoading}
              >
                Execute Template
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default TemplateParameterControls;
